import React, { useMemo, useState, useCallback } from 'react';
import Divider from '@mui/material/Divider';
import { useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import styled, { css } from 'styled-components';
import { ToggleButtonGroup } from './Input';
import useTransition from '../hooks/useTransition';
import SubmitButton from './SubmitButton';
import {
  IPolarisInterviewOption,
  IPolarisInterviewQuestion,
  QuestionType,
} from 'src/packages/polaris/types/interviews';

import useOptional from '../hooks/useOptional';
import useIsPolaris from '../hooks/useIsPolaris';

const parseQuestionForMultiSelectLimit = (text?: string) => {
  if (!text) {
    return null;
  }

  // Use a regex to find "select up to" followed by a number
  const match = text.match(/select up to (\d+)/i);

  // If match is found, return the number as an integer
  if (match) {
    return parseInt(match[1], 10);
  }

  // Return null if the pattern is not found
  return null;
};

const parseQuestionForChiefComplaintText = (text?: string) => {
  return text?.includes?.('bothering you the most?') || false;
};

export const StyledToggleButtonGroup = styled(ToggleButtonGroup)<{
  $arrowStyle?: boolean;
  $selectedTagStyle?: boolean;
}>`
  ${({ $arrowStyle }) =>
    $arrowStyle &&
    css`
      button.MuiToggleButton-root {
        min-height: 54px;
        padding-right: 36px;
      }
      .MuiRadio-root {
        display: none;
      }

      button:after {
        content: '→';
        color: ${({ theme }) => theme.palette.primary.main};
        position: absolute;
        right: 14px;
        font-size: 24px;
      }
    `}

  ${({ $selectedTagStyle }) =>
    $selectedTagStyle &&
    css`
      button.MuiToggleButton-root {
        min-height: 54px;
        padding-right: 118px;
        background-color: white;
        border-color: ${({ theme }) => theme.palette.gray[10]};

        .MuiCheckbox-root {
          display: none;
        }

        &:focus,
        &:hover {
          background-color: ${({ theme }) => theme.palette.gray[0]};
        }

        &.Mui-selected {
          background-color: white;
          border-color: ${({ theme }) => theme.palette.status.success} !important;

          &:hover {
            background-color: ${({ theme }) => theme.palette.gray[0]};
          }

          &:after {
            content: attr(data-tag);
            color: ${({ theme }) => theme.palette.text.primary};
            position: absolute;
            right: 14px;
            font-size: 12px;
            background-color: ${({ theme }) => theme.palette.teal[10]};
            border-radius: 100px;
            padding: 2px 8px;
            text-transform: none;
          }
        }
      }
    `}
`;

const formatOptions = (options: IPolarisInterviewOption[], isChiefComplaintQuestion?: boolean) =>
  options.map((o) => ({
    label: o.text || '',
    subText: o.subText || '',
    value: o.token || '',
    media: o.media || '',
    mediaAltText: o.media_alttext || '',
    ...(isChiefComplaintQuestion
      ? {
          'data-tag': 'Main symptom',
          media: '',
        }
      : {}),
  }));

const Options = ({
  question,
  hideTopDivider,
  isMultiSelect,
  showSubmitButton,
  onQuestionSubmit,
  onGoBackClick,
}: {
  question: IPolarisInterviewQuestion;
  hideTopDivider?: boolean;
  isMultiSelect: boolean;
  showSubmitButton?: boolean;
  onQuestionSubmit?: (questionIndex: number) => void;
  onGoBackClick?: () => void;
}) => {
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('Please select an option above.');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { options = [] } = question;
  const theme = useTheme();
  const transition = useTransition();
  const isOptional = useOptional();
  const isPolaris = useIsPolaris();

  const multiSelectLimit = useMemo(
    () => parseQuestionForMultiSelectLimit(question?.text),
    [question?.text],
  );

  const isChiefComplaintQuestion = useMemo(
    () => parseQuestionForChiefComplaintText(question?.text),
    [question?.text],
  );

  const [value, setValue] = useState<string | string[]>(
    isMultiSelect
      ? question.answer?.map((ans: any) => ans.token)
      : question.answer?.[0]?.token || '',
  );

  const groupedOptions = useMemo(() => {
    const preselected = options.filter((opt) => opt.preselected === true);
    const mainOptions = options.filter((opt) => opt.preselected == null);
    if (!!preselected.length && !question.answer?.length) {
      setValue(preselected.map((o: any) => o.token));
    }
    const allOptions = [...preselected, ...mainOptions];

    const exclusive =
      !isMultiSelect || isChiefComplaintQuestion
        ? null
        : formatOptions(allOptions.filter((o) => o.exclusive));

    return {
      mainOptions: isMultiSelect
        ? formatOptions(
            allOptions.filter((o) => !o.exclusive),
            isChiefComplaintQuestion,
          )
        : formatOptions(allOptions),
      exclusive: exclusive,
    };
  }, [options, isChiefComplaintQuestion]);

  const shouldShowSubmitButton = showSubmitButton || isMultiSelect;

  const handleSubmit = useCallback(
    (val?: string) => {
      const selectedValue = val || value;
      if (!isOptional && !selectedValue?.length) {
        return setIsError(true);
      }

      if (multiSelectLimit && selectedValue?.length > multiSelectLimit) {
        setIsError(true);
        setErrorMessage(
          `Oops! Please limit your selection to ${multiSelectLimit}. Don’t worry—we’ll still consider all the symptoms you entered; we just need to know which ones are most important to you.`,
        );
        return;
      }

      setIsSubmitting(true);

      if (question.index !== undefined) {
        onQuestionSubmit?.(question.index);
      }

      transition({
        data: {
          options: options?.filter((opt) =>
            isMultiSelect ? selectedValue?.includes(opt.token || '') : opt.token === selectedValue,
          ),
          question,
          isPolaris,
        },
      });
    },

    [
      isMultiSelect,
      isOptional,
      isPolaris,
      multiSelectLimit,
      onQuestionSubmit,
      options,
      question,
      transition,
      value,
    ],
  );

  const onChange = useCallback(
    (_e: any, val: any) => {
      if (isSubmitting) {
        return;
      }
      setIsError(false);
      if (!isMultiSelect) {
        setValue(val);
        if (question.index !== 0 && !shouldShowSubmitButton) {
          setIsSubmitting(true);
          setTimeout(() => handleSubmit(val), theme.transitions.duration.standard);
        }
      } else {
        const additionalOptionValues = groupedOptions.exclusive?.map((ao) => ao.value) || [];
        // Filter out e.g. "None of the above" type answers
        const filteredValues = (val as string[])?.filter(
          (v) => !additionalOptionValues.includes(v),
        );
        setValue(filteredValues);
      }
    },
    [
      isSubmitting,
      isMultiSelect,
      question.index,
      shouldShowSubmitButton,
      theme.transitions.duration.standard,
      handleSubmit,
      groupedOptions.exclusive,
    ],
  );

  const onExclusiveOptionChange = useCallback(
    // eslint-disable-next-line @typescript-eslint/no-shadow
    (e: React.SyntheticEvent<HTMLElement>, value: any) => {
      setIsError(false);
      const val = (e.currentTarget as HTMLButtonElement)?.value;
      setValue(value.length ? [val] : []);
    },
    [],
  );

  return (
    <>
      {isMultiSelect && (
        <Typography className="multiselect-label" variant="body2" sx={{ marginBottom: '12px' }}>
          {multiSelectLimit ? `Select up to ${multiSelectLimit}` : 'Please mark all that apply'}
        </Typography>
      )}

      <StyledToggleButtonGroup
        $arrowStyle={question.choice === QuestionType.ArrowButton}
        $selectedTagStyle={isChiefComplaintQuestion}
        disabled={isSubmitting}
        fullWidth
        hasError={isError}
        multiSelect={isMultiSelect}
        onChange={onChange}
        options={groupedOptions.mainOptions}
        value={value}
      />
      {/* These are e.g. "None of the above" or "Unsure" */}
      {!!groupedOptions.exclusive?.length && (
        <>
          <Divider sx={{ margin: '20px 0', borderColor: theme.palette.gray[40] }} />
          <StyledToggleButtonGroup
            $arrowStyle={question.choice === QuestionType.ArrowButton}
            disabled={isSubmitting}
            fullWidth
            hasError={isError}
            multiSelect={isMultiSelect}
            onChange={onExclusiveOptionChange}
            options={groupedOptions.exclusive}
            value={value}
          />
        </>
      )}
      {
        <SubmitButton
          errorMessage={errorMessage}
          disabled={isSubmitting}
          hideTopDivider={hideTopDivider}
          isError={isError}
          onClick={handleSubmit}
          onGoBackClick={onGoBackClick}
          text={!value?.length && isOptional ? 'Skip' : 'Next'}
        />
      }
    </>
  );
};

export default Options;
